/**
 * Kingdom Factor - Legal Documents Page
 * Single page with tabs to switch between Privacy Policy, Terms of Service, etc.
 */

import { router } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { sanitizeContentHtml } from "@/lib/sanitize-html"
import { FileText } from "lucide-react"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

export default function Legal({ seo, documents = [], defaultTab = "privacy-policy", settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  // Handle tab change - update URL for bookmarking/sharing
  const handleTabChange = (value) => {
    router.get("/legal", { tab: value }, {
      preserveState: true,
      preserveScroll: true,
      replace: true
    })
  }

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="legal" scheduleCallUrl={scheduleCallUrl} />

        {/* Hero / Header */}
        <section className="bg-zinc-50 pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-kf-gradient rounded-full mb-6">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
              LEGAL
            </h1>
            <p className="text-xl text-zinc-600">
              Our policies and terms of service
            </p>
          </div>
        </section>

        {/* Legal Documents with Tabs */}
        <section className="bg-white py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            {documents.length > 0 ? (
              <Tabs
                defaultValue={defaultTab}
                onValueChange={handleTabChange}
                className="w-full"
              >
                <TabsList className="w-full justify-start mb-8 bg-zinc-100 p-1.5 rounded-lg h-auto flex-wrap gap-1">
                  {documents.map((doc) => (
                    <TabsTrigger
                      key={doc.slug}
                      value={doc.slug}
                      className="flex-1 sm:flex-none px-6 py-3 text-sm font-semibold uppercase tracking-wider text-zinc-600 data-[state=active]:bg-white data-[state=active]:text-kf-red data-[state=active]:shadow-sm rounded-md transition-all hover:text-zinc-900"
                    >
                      {doc.title}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {documents.map((doc) => (
                  <TabsContent key={doc.slug} value={doc.slug}>
                    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-8">
                      <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-6">
                        {doc.title}
                      </h2>
                      {/* Render HTML content from TipTap */}
                      <div
                        className="prose prose-zinc max-w-none prose-headings:font-display prose-a:text-kf-red prose-a:no-underline hover:prose-a:underline"
                        dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(doc.content || "") }}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <p>No legal documents available at this time.</p>
              </div>
            )}
          </div>
        </section>

        <Footer activePage="legal" />
      </div>
    </>
  )
}
